import { Collapse, Divider, Flex } from "antd";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { fetchSongToolsSigninToken } from "shared/service/StrmService";
import imgBoostCTABGSrc from "shared/assets/images/boost-cta-bg.png";
import imgBoostProCTABGSrc from "shared/assets/images/boost-pro-cta-bg.png";
import CardCTA from "shared/components/card-cta/CardCTA";
import "./Promotion.scss";
import { useSelector } from "react-redux";
import { ArtistState } from "explorer/reducers/artistas";
import MenuFriendlyIframe from "shared/components/menu-friendly-iframe/MenuFriendlyIframe";
import { useFlags } from "launchdarkly-react-client-sdk";
import { openNewTab } from "shared/helpers/window-helpers";

const PromotionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
  >
    <ellipse
      cx="14.0833"
      cy="7.41667"
      rx="3.33333"
      ry="6.66667"
      stroke="url(#paint0_linear_21855_5198)"
      stroke-width="1.5"
      stroke-linecap="round"
    />
    <path
      d="M14.0833 0.75C11.4974 0.75 6.1376 2.73134 3.05921 3.96143C1.64952 4.52473 0.75 5.8986 0.75 7.41667C0.75 8.93473 1.64952 10.3086 3.05921 10.8719C6.1376 12.102 11.4974 14.0833 14.0833 14.0833"
      stroke="url(#paint1_linear_21855_5198)"
      stroke-width="1.5"
      stroke-linecap="round"
    />
    <path
      d="M8.25 17.4163L6.63062 16.5249C4.86747 15.5544 3.87212 13.5942 4.12122 11.583"
      stroke="url(#paint2_linear_21855_5198)"
      stroke-width="1.5"
      stroke-linecap="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_21855_5198"
        x1="14.0833"
        y1="0.75"
        x2="14.0833"
        y2="14.0833"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#F9B3DE" />
        <stop offset="1" stop-color="#ADABFF" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_21855_5198"
        x1="7.41667"
        y1="0.75"
        x2="7.41667"
        y2="14.0833"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#F9B3DE" />
        <stop offset="1" stop-color="#ADABFF" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_21855_5198"
        x1="6.16667"
        y1="11.583"
        x2="6.16667"
        y2="17.4163"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#F9B3DE" />
        <stop offset="1" stop-color="#ADABFF" />
      </linearGradient>
    </defs>
  </svg>
);

const FireIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="20"
      viewBox="0 0 18 20"
      fill="none"
    >
      <path
        d="M8.073 1.02441C11.6463 3.17415 14.9556 6.84835 16.0427 10.4063C16.5916 12.2027 16.5943 14.0491 15.6677 15.6387C14.7419 17.2269 12.9759 18.408 10.3093 19.0625L8.06616 19.6123L8.38062 19.998L5.79565 19.0361C3.38006 18.1378 1.6589 16.4502 1.02026 14.3779C0.377109 12.2904 0.87332 9.94712 2.62964 7.85156C5.15518 4.83821 6.2057 4.17883 6.96362 1.46484L7.22925 0.516602L8.073 1.02441ZM10.3171 10.2051C10.2545 11.5782 9.71462 12.7169 9.16577 13.3906L8.16089 14.624L7.84839 13.0645C7.64723 12.0587 7.11515 11.5755 6.50562 11.2354C6.38642 12.4192 5.74235 13.1935 5.50464 13.6689C4.54976 15.5787 5.86605 16.9124 6.63843 17.8604L8.03589 19.5752L9.56812 17.8379C10.6467 16.614 11.5799 15.2038 11.7156 13.7334C11.8123 12.685 11.5102 11.4899 10.3171 10.2051Z"
        stroke="url(#paint0_linear_22712_146)"
        stroke-width="1.5"
      />
      <defs>
        <linearGradient
          id="paint0_linear_22712_146"
          x1="8.58407"
          y1="1.66699"
          x2="8.58407"
          y2="18.3337"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#F680C8" />
          <stop offset="1" stop-color="#ADABFF" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const Promotion: React.FC = () => {
  const { promotionCatapult } = useFlags();
  const [songToolsToken, setSongToolsToken] = useState<string>();

  const [showBoostPromotion, setShowBoostPromotion] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);

  const boostParams = useMemo(() => {
    return {
      title: "Boost",
      subtitle: t("pages.Promotion.boostSubtitle"),
      description: t("pages.Promotion.boostDescription"),
      mainImageSrc: imgBoostCTABGSrc,
      actions: (
        <>
          <button
            className="botao-roxo"
            onClick={() => setShowBoostPromotion(true)}
          >
            <FireIcon /> {t("pages.Promotion.boostNow")}
          </button>
        </>
      ),
    };
  }, [t]);

  const boostProParams = useMemo(() => {
    const onClickBoostPro = () => {
      const lang = (i18n.language || "en").toLowerCase();
      let url = "https://promotion.strmmusic.com/en/login";

      if (lang.startsWith("pt")) {
        url = "https://promotion.strmmusic.com/pt/entrar";
      } else if (lang.startsWith("es")) {
        url = "https://promotion.strmmusic.com/es/iniciar-sesion";
      }
      console.log(url);
      openNewTab(url);
    };
    return {
      title: "Boost Pro",
      subtitle: t("pages.Promotion.boostProSubtitle"),
      description: t("pages.Promotion.boostProDescription"),
      mainImageSrc: imgBoostProCTABGSrc,
      actions: (
        <>
          <button className="botao-roxo" onClick={onClickBoostPro}>
            <FireIcon /> {t("pages.Promotion.boostProNow")}
          </button>
        </>
      ),
    };
  }, [t, i18n]);

  const faqSongToolsList = useMemo(
    () =>
      t("pages.Promotion.faqSongTools", {
        returnObjects: true,
      }) as Array<{
        question: string;
        answer: string;
      }>,
    [t],
  );

  const faqCatapultList = useMemo(
    () =>
      t("pages.Promotion.faqCatapult", {
        returnObjects: true,
      }) as Array<{
        question: string;
        answer: string;
      }>,
    [t],
  );

  const ctaList = useMemo(() => {
    const stage = userArtist?.stage ?? 0;
    const isBoostRecommended = stage < 5;

    const tag = (
      <div className="badge-cta">{t("pages.Promotion.recommendedForYou")}</div>
    );
    const label = {
      icon: <PromotionIcon />,
      label: t("pages.Promotion.ctaTitle"),
    };
    if (isBoostRecommended) {
      const first: any = { ...boostParams, tag, ...label };
      const list = [first];
      if (promotionCatapult) list.push(boostProParams);
      return list;
    } else {
      if (promotionCatapult) {
        return [{ ...boostProParams, tag, ...label }, boostParams];
      } else {
        return [{ ...boostParams, tag, ...label }];
      }
    }
  }, [boostParams, boostProParams, userArtist, promotionCatapult, t]);

  const finalContentClasses = ["content", "content--final"];

  useEffect(() => {
    const loadScript = (src: string, attrs: Record<string, string> = {}) =>
      new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        Object.entries(attrs).forEach(([key, value]) =>
          script.setAttribute(key, value),
        );
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.body.appendChild(script);
      });

    const inlineScript = document.createElement("script");
    inlineScript.innerHTML = `
      var isMobile = false;
      var eventMethod = window.addEventListener ? "addEventListener" : "attachEvent";
      var eventer = window[eventMethod];
      var messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";
      eventer(messageEvent, function (e) { processParentEvent(e); });
    `;
    document.body.appendChild(inlineScript);

    Promise.all([
      loadScript("https://strm.songtools.io/js/wixgetcontent.js?v=1738787616"),
      loadScript(
        "https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.5.1.min.dc5e7f18c8.js?site=6706d724bb0e4c6b5a5c849b",
        {
          integrity: "sha256-9/aliU8dGd2tb6OSsuzixeV4y/faTqgFtohetphbbj0=",
          crossorigin: "anonymous",
        },
      ),
    ]).then(() => {});

    return () => {
      document.body.removeChild(inlineScript);
    };
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response: any = await fetchSongToolsSigninToken();

        setSongToolsToken(response);
      } catch (error) {
        console.error("Failed to fetch SongTools signin token", error);
      }
    };

    fetchToken();
  }, []);

  useEffect(() => {
    if (songToolsToken) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Promotions.SONGTOOLS_WHITELABEL,
      });
    }
  }, [songToolsToken]);

  if (!songToolsToken && showBoostPromotion) return null;

  if (showBoostPromotion)
    return (
      <MenuFriendlyIframe>
        <iframe
          title="songtools-widget"
          id="iframeWidget"
          frameBorder="0"
          width="100%"
          height="900"
          src={`https://widgets.songtools.io/v1/CampaignTestX?app-key=CA604044B66447399364523482EC42AB&autodetect=1&jwt=${songToolsToken}`}
          style={{
            backgroundColor: "transparent",
            height: "100dvh",
            border: "0px",
            margin: "0",
            padding: "0",
            overflow: "hidden",
          }}
          allow="clipboard-write"
          scrolling="no"
        ></iframe>
      </MenuFriendlyIframe>
    );

  return (
    <div className="promotion">
      <div className="content-wrapper">
        <div className={finalContentClasses.join(" ")}>
          <Flex className="header" vertical>
            <h1 className="heading-h3">{t("pages.Promotion.title")}</h1>
            <h3 className="body-regular subtitle">
              {t("pages.Promotion.subtitle")}
            </h3>
          </Flex>
          <Divider className="divider" />
          {ctaList.map((cta, index) => (
            <CardCTA key={index} {...cta} />
          ))}
          <Divider className="divider" />
          <Flex className="faq" vertical>
            <Flex className="header-faq" vertical>
              <h2 className="title">FAQ</h2>
              <h2 className="subtitle">
                {t("pages.DistributionCenter.faqTitle")}
              </h2>
            </Flex>
            <h3>Boost</h3>
            <Collapse
              className="faq-collapse"
              bordered={false}
              expandIconPosition="end"
              items={faqSongToolsList?.map((faq: any, index: number) => ({
                key: index.toString(),
                label: faq.question,
                children: (
                  <Trans
                    i18nKey={`pages.Promotion.faqSongTools.${index}.answer`}
                  />
                ),
              }))}
            />
            {promotionCatapult && (
              <>
                <h3 style={{ marginTop: 20 }}>Boost Pro</h3>
                <Collapse
                  className="faq-collapse"
                  bordered={false}
                  expandIconPosition="end"
                  items={faqCatapultList?.map((faq: any, index: number) => ({
                    key: index.toString(),
                    label: faq.question,
                    children: (
                      <Trans
                        i18nKey={`pages.Promotion.faqCatapult.${index}.answer`}
                      />
                    ),
                  }))}
                />
              </>
            )}
          </Flex>
        </div>
      </div>
    </div>
  );
};

export default Promotion;
